import { NgClass, AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatLabel } from '@angular/material/form-field';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { LetDirective, PushPipe } from '@ngrx/component';
import { createSelector, Store } from '@ngrx/store';
import Color from 'color';
import Fuse, { IFuseOptions } from 'fuse.js';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  exhaustMap,
  filter,
  map,
  mergeMap,
  startWith,
  Subscription,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

import { ConversationDetailInput } from '@ninety/platform/models/_shared/detail-view-input';
import { RoleCode } from '@ninety/platform/models/_shared/role-code';
import { Team } from '@ninety/platform/models/_shared/team';
import { User } from '@ninety/platform/models/_shared/user';
import { DirectoryUserStatus } from '@ninety/platform/models/directory/directory-user-status';
import { Scopes } from '@ninety/platform/models/entitlements/entitlements-model';
import { Conversation } from '@ninety/platform/models/feedback/conversation';
import { IntervalCode } from '@ninety/platform/models/issues/interval-code';
import { MasteryFilterStatus } from '@ninety/platform/models/mastery/mastery-status';
import type { RockStatus } from '@ninety/platform/models/rocks/rock-status';
import {
  ExcelExportType,
  FilterService,
  AuxiliaryRouterOutletService,
  HelperService,
  StateService,
  extractValueFromStore,
} from '@ninety/platform/services';
import {
  LayoutsIssuesEvents,
  LayoutsTodosEvents,
  selectHasCompletedIssues,
  selectIssuesArchiveTypeFilter,
  selectShowIssuesTrash,
  SpinnerService,
  FeatureFlagFacade,
  FeatureFlagKeys,
  selectFeatureFlag,
  teamAll,
  TeamListModel,
  teamNone,
  TeamListStateActions,
  TeamSelectors,
  UserListStateActions,
  selectFeatureEnabled,
  selectHasAnyHelpfulPermission,
  selectHasHelpfulPermission,
  selectCompany,
  selectCompanyColorBranding,
  selectIsInMeeting,
  CurrentUserSelectors,
  selectCurrentUserIsAdminOrOwner,
  selectCurrentUserIsLite,
  selectCurrentUserIsManagerOrAbove,
  selectCurrentUserIsObserver,
  selectLanguage,
  selectUserFilterOptions,
} from '@ninety/platform/state';
import { BillingStateActions } from '@ninety/platform/state/app-global/billing/billing-state.actions';
import {
  TerraQuickFilterModule,
  TerraSearchInputModule,
  TerraDescriptionModule,
  TerraOptionModule,
  TerraIconModule,
  TerraAvatarModule,
  TerraSwitchModule,
  TerraDividerModule,
} from '@ninety/terra';
import {
  ButtonComponent,
  DisplayNameOrEmailPipe,
  FilterUsersByRolePipe,
  NinetyCmButtonComponent,
  NinetyCmPopoverComponent,
  NinetyCmPopoverTriggerDirective,
  NinetyTooltipDirective,
  SortByUserNamePipe,
  StopPropagationDirective,
  UserInitialsPipe,
} from '@ninety/ui/core';
import { QuickFilterOption } from '@ninety/ui/legacy/components/index';
import { RockStatusService } from '@ninety/ui/legacy/core/services/rock-status.service-v2';
import { UserService } from '@ninety/ui/legacy/core/services/user.service';
import { DetailService } from '@ninety/web/pages/detail-view/_services/detail.service';
import { FeedbackService } from '@ninety/web/pages/feedback/_shared/feedback.service';
import { selectShowCompletedConversations } from '@ninety/web/pages/feedback/_state/conversation-list/conversation-list.selectors';
import { FormalConversationSettingsComponent } from '@ninety/web/pages/feedback/formal-conversation-settings/formal-conversation-settings.component';
import {
  selectHasCompletedHeadlines,
  selectIsHeadlinesTrashView,
} from '@ninety/web/pages/headlines/_state/headlines/headlines-state.selectors';
import { MeetingsPageActions } from '@ninety/web/pages/meeting/_state/meetings.actions';
import { MeetingsStateSelectors } from '@ninety/web/pages/meeting/_state/meetings.selectors';
import {
  RocksStateSelectors,
  selectArchived,
  selectPlanningBoardPageView,
} from '@ninety/web/pages/rocks/_state/rocks-state.selectors';
import { TeamSearchComponent } from '@ninety/web/pages/settings/company/teams/search/team-search/team-search.component';
import { TeamTodoSelectors } from '@ninety/web/pages/todos/_state/team/team-todo.selectors';
import { VtoService } from '@ninety/web/pages/vto/services/vto.service';

import { GridLayoutSelectors } from '../../grid-layout/_state/grid-layout-state.selectors';
import { GridLayoutToolbarButtonsComponent } from '../../grid-layout/components/grid-layout-toolbar-buttons/grid-layout-toolbar-buttons.component';

import { LayoutActionsGroupComponent } from './components/layout-actions-group/layout-actions-group.component';

interface SearchResultType extends TeamListModel {
  canEdit?: boolean;
  dontShowInResults?: boolean;
}

/**
 * "Inside a meeting" for toolbar purposes: on a meeting route (covers the in-meeting sections and
 * View Tools, which live under /meeting/:id) or with a meeting loaded in state (belt and braces —
 * e.g. mid-meeting navigation states where the URL check alone could miss).
 */
const selectIsMeetingContext = createSelector(
  selectIsInMeeting,
  MeetingsStateSelectors.selectCurrentMeeting,
  (isMeetingRoute, currentMeeting) => isMeetingRoute || !!currentMeeting
);

/**
 * Is the company's brand color light enough that white-on-brand would be unreadable?
 *
 * SCSS can't answer this: `terra.color('brand')` is `var(--brand)`, which only exists at runtime —
 * `BrandingThemeDirective` writes it to `document.body`. So we branch here instead, on the same
 * `--primaryAccent` hex from the store that Terra derives every `--brand*` var from, and expose the
 * answer as a class the stylesheet switches `text` / `text-invert` on. Defaults to "not light"
 * (white chevron), matching the toggle's pre-existing hover treatment.
 */
const selectBrandColorIsLight = createSelector(selectCompanyColorBranding, theme => {
  const hex = theme?.properties?.['--primaryAccent'];
  if (!hex) return false;

  try {
    return Color(hex).isLight();
  } catch {
    return false;
  }
});

@Component({
  selector: 'ninety-filters-toolbar',
  templateUrl: './filters-toolbar.component.html',
  styleUrls: ['./filters-toolbar.component.scss', './filters-toolbar.terrafy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    DatePipe,
    LetDirective,
    NgClass,
    PushPipe,
    FormsModule,
    MatIconButton,
    MatToolbar,
    MatTooltip,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatLabel,
    NinetyCmPopoverTriggerDirective,
    StopPropagationDirective,
    SortByUserNamePipe,
    UserInitialsPipe,
    FilterUsersByRolePipe,
    DisplayNameOrEmailPipe,
    ButtonComponent,
    GridLayoutToolbarButtonsComponent,
    LayoutActionsGroupComponent,
    NinetyCmPopoverComponent,
    NinetyCmButtonComponent,
    TerraQuickFilterModule,
    TerraSearchInputModule,
    TerraDescriptionModule,
    TerraOptionModule,
    TerraIconModule,
    TerraAvatarModule,
    TerraSwitchModule,
    TerraDividerModule,
    NinetyTooltipDirective,
  ],
})
export class FiltersToolbarComponent implements OnInit, OnDestroy {
  private readonly stateService = inject(StateService);
  readonly filterService = inject(FilterService);
  readonly rockStatusService = inject(RockStatusService);
  readonly spinnerService = inject(SpinnerService);
  readonly userService = inject(UserService);
  readonly vtoService = inject(VtoService);
  readonly helperService = inject(HelperService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly legacyDialog = inject(MatDialog);
  private readonly detailService = inject<DetailService<ConversationDetailInput>>(DetailService);
  readonly auxRouterService = inject(AuxiliaryRouterOutletService);
  readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly featureFlagFacade = inject(FeatureFlagFacade);

  readonly isMeeting = input(false);
  @Input() isFeedback?: boolean;

  subscriptions = new Subscription();
  RoleCode = RoleCode;
  MasteryFilterStatus = MasteryFilterStatus;
  DirectoryUserStatus = DirectoryUserStatus;

  private readonly searchTerm$ = new BehaviorSubject<string>('');

  @ViewChild(TeamSearchComponent) teamSearchComponent: TeamSearchComponent;

  isEditingMy90Layout$ = this.store.select(GridLayoutSelectors.selectIsLayoutModeEnabled);
  printOrDownloadFeature$ = this.store.select(selectFeatureEnabled(Scopes.printOrDownload));
  showCompletedConversations$ = this.store.select(selectShowCompletedConversations);
  private readonly filterBarTeamLite$ = this.store.select(TeamSelectors.selectFilterBarTeamLite);

  quarterlyQuestionsEnabled = false;

  usersOptions$ = this.store.select(selectUserFilterOptions);

  readonly data$ = {
    company: this.store.select(selectCompany),
    isAdminOrOwner: this.store.select(selectCurrentUserIsAdminOrOwner),
    isLiteUser: this.store.select(selectCurrentUserIsLite),
    isManagerOrAbove: this.store.select(selectCurrentUserIsManagerOrAbove),
    isObserver: this.store.select(selectCurrentUserIsObserver),
    options: this.filterService.options$,
    isFetchingData: this.filterService.isFetchingData$,
    showArchived: this.filterService.showArchived$,
    showCompleted: this.filterService.showCompleted$,
    selectedUserId: this.filterService.selectedUserId$,
    selectedUserStatusId: this.filterService.selectedUserStatusId$,
    selectedRockFilter: this.filterService.selectedRockStatusCode$,
    selectedMasteryStatus: this.filterService.selectedMasteryStatus$,
    selectedOwnerId: this.filterService.selectedOwnerId$,
    showResponsiveFilters: this.filterService.filtersToolbarExpanded$,
    archivedVto: this.vtoService.archivedVto$,
    teamVto: this.vtoService.teamVto$,
    sltVto: this.vtoService.sltVto$,
    language: this.store.select(selectLanguage),
    excelDownloadBatch2FeatureFlag: this.store.select(selectFeatureFlag(FeatureFlagKeys.webExcelDownloadBatch2)),
    weeklyFeatureFlag: this.store.select(selectFeatureFlag(FeatureFlagKeys.webWeeklyConversations)),
    brandIsLight: this.store.select(selectBrandColorIsLight),
    isSlt: this.store.select(TeamSelectors.selectFilterBarTeamIsSlt),
    selectedTeam: this.store.select(TeamSelectors.selectFilterBarTeamLite),
    userTeams: this.store.select(CurrentUserSelectors.selectTeams),
    usersFilterOptions: this.usersOptions$,
    selectedUsersFilterOptions: this.usersOptions$.pipe(
      startWith([]),
      map(options => options?.filter(option => option.selected).map(option => option.item) ?? [])
    ),
    filteredTeams: combineLatest([
      this.store.select(CurrentUserSelectors.selectTeams),
      this.searchTerm$,
      this.filterService.options$,
      this.filterBarTeamLite$,
    ]).pipe(
      map(([teams, searchTerm, options, selectedTeam]): SearchResultType[] => {
        return this.filterTeams(searchTerm, teams, options, selectedTeam);
      })
    ),
    rocksQuickFilteredTeams: this.store.select(CurrentUserSelectors.selectTeams).pipe(
      withLatestFrom(this.filterBarTeamLite$),
      map(([teams, selectedTeam]): QuickFilterOption<Team>[] => {
        return teams.map(team => ({
          displayText: team.name,
          id: team._id,
          item: team,
          selected: selectedTeam?._id ? team._id === selectedTeam._id : false,
        }));
      })
    ),
    rocksQuickFilteredStatuses: this.filterService.options$.pipe(
      map(options => {
        if (options.rocksQuickFilters) {
          const statuses: QuickFilterOption<RockStatus | 'all'>[] = this.rockStatusService.filterBarStatuses.map(
            status => {
              return { displayText: status.label, id: status.code, item: status, selected: false };
            }
          );
          statuses.unshift({
            displayText: 'All',
            id: 'all',
            item: 'all',
            selected: true,
          } as QuickFilterOption<'all'>);
          return statuses;
        }
      })
    ),
    teams: this.store.select(CurrentUserSelectors.selectTeams),
    searchTerm: this.searchTerm$,
    allowedRocksDraftStatus: this.store.select(selectPlanningBoardPageView).pipe(
      combineLatestWith(this.store.select(selectArchived)),
      map(([isPlanningBoardPageView, showArchived]) => isPlanningBoardPageView || showArchived)
    ),
    hasCompletedRocks: this.store.select(RocksStateSelectors.selectHasCompletedRocks),
    hasCompletedIssues: this.store.select(selectHasCompletedIssues),
    hasCompletedTodos: this.store.select(TeamTodoSelectors.selectHasCompletedTodos),
    hasCompletedHeadlines: this.store.select(selectHasCompletedHeadlines),
    isHeadlinesTrashcanEnabled: this.store.select(selectFeatureFlag(FeatureFlagKeys.webHeadlinesTrashcan)),
    isHeadlinesTrashView: this.store.select(selectIsHeadlinesTrashView),
    isIssuesTrashView: this.store.select(selectShowIssuesTrash),
    issuesArchiveTypeFilter: this.store.select(selectIssuesArchiveTypeFilter),
    isTodosTrashcanEnabled: this.store.select(selectFeatureFlag(FeatureFlagKeys.webTodosTrashcan)),
    isTodosTrashView: this.store.select(TeamTodoSelectors.selectShowTrash),
    isMeetingContext: this.store.select(selectIsMeetingContext),
  };

  readonly hasDataUploadPermission$ = this.store.select(selectHasHelpfulPermission('dataUpload')).pipe(
    combineLatestWith(
      this.store.select(selectCurrentUserIsAdminOrOwner),
      this.featureFlagFacade.getFlag(FeatureFlagKeys.clientFacingDataUpload)
    ),
    map(([hasDataUploadPermissions, isAdminOrOwner, flag]) => (flag && isAdminOrOwner) || hasDataUploadPermissions)
  );

  readonly isHelpful$ = this.store.select(selectHasAnyHelpfulPermission);

  ExcelExportType = ExcelExportType;
  protected readonly IntervalCode = IntervalCode;

  locale = this.stateService.locale;
  pageTitle$ = this.stateService.pageTitle$.asObservable();
  protected readonly showMeetingNav = toSignal(this.stateService.toggleMeetingSideNavB$, { initialValue: true });
  protected readonly meetingNavCollapsed = computed(() => this.isMeeting() && !this.showMeetingNav());

  expandMeetingNav(): void {
    this.stateService.toggleMeetingSideNav(true);
    // Analytics: MeetingsAnalyticsEffects maps this to the Segment `meeting_navigation_toggled` event.
    this.store.dispatch(MeetingsPageActions.meetingNavToggled({ expanded: true }));
  }

  ngOnInit() {
    this.subscriptions.add(
      this.featureFlagFacade
        .getFlag(FeatureFlagKeys.quarterlyQuestionSettings)
        .pipe(take(1))
        .subscribe(flag => {
          this.quarterlyQuestionsEnabled = flag;
        })
    );

    let conversation: Conversation;

    this.subscriptions.add(
      this.detailService.conversationOpenFormalSettingsDialog$
        .pipe(
          tap(c => (conversation = c)),
          exhaustMap(conversation =>
            this.legacyDialog
              .open(FormalConversationSettingsComponent, {
                autoFocus: false,
                data: conversation ? { conversation, quarterlyQuestionsEnabled: this.quarterlyQuestionsEnabled } : null,
              })
              .afterClosed()
          ),
          filter(questions => !!questions),
          tap(questions => {
            if (conversation.type === 'quarterly') {
              conversation.quarterlyQuestions = questions;
            } else {
              conversation.meetingQuestions = questions;
            }
          }),
          mergeMap(questions => this.feedbackService.updateConversationQuestions(conversation._id, questions))
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  parsePercent(number: number): string {
    return `${Math.round(number * 100)}%`;
  }

  selectTeam(team: Team): void {
    this.store.dispatch(TeamListStateActions.changeFilterBarTeam({ id: team._id }));
  }

  /** Issues Archive tab: the Type pill publishes a bridge event that issues-v2 handles. */
  selectIssuesArchiveType(intervalCode: IntervalCode | 'all'): void {
    this.store.dispatch(LayoutsIssuesEvents.archiveTypeFilterChanged({ intervalCode }));
  }

  selectTeamByQuickFilter(filters: QuickFilterOption<Team>[]): void {
    const selectedTeam = filters.find(filter => filter.selected);

    if (selectedTeam) {
      this.store.dispatch(TeamListStateActions.changeFilterBarTeam({ id: selectedTeam.id }));
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trackById = (_i: number, item: any): string => item._id;

  teamsAreSame(option, value): boolean {
    return option?._id === value?._id;
  }

  onSearchChanged(term: string): void {
    this.searchTerm$.next(term);
  }

  teamSearchVal = '';

  filterTeams(
    searchTerm: string,
    teams: TeamListModel[],
    filterOptions,
    selectedTeam: TeamListModel
  ): SearchResultType[] {
    const fuseOptions: IFuseOptions<SearchResultType> = {
      keys: ['name'],
      includeScore: true,
      threshold: 0.3, // Adjust this value for fuzziness; lower values mean stricter matching
    };
    if (filterOptions?.allTeamsOption || filterOptions?.allTeamsSelectableOption) {
      const allTeams = { ...teamAll, canEdit: false };
      teams = [allTeams, ...teams];
    } else if (teams.length === 0) {
      // This may be completely unneeded.  Everyone should be on at least one team.
      const noneTeam = { ...teamNone, canEdit: false };
      teams = [noneTeam];
    }

    if (searchTerm.trim() === '') {
      return teams;
    }

    // Index after prepending so the All/None pseudo-teams are searchable too
    const fuse = new Fuse<SearchResultType>(teams, fuseOptions);
    const filteredTeams = fuse.search(searchTerm).map(result => result.item);
    const selectedTeamIsNotInResults = !filteredTeams.find(x => selectedTeam._id === x._id);
    if (selectedTeamIsNotInResults) {
      // Add selected team back to filtered results so that the mat select
      // does not blank out the currently selected item
      filteredTeams.push({ ...selectedTeam, dontShowInResults: true });
    }

    return filteredTeams;
  }

  onTeamDropdownOpened(event: boolean) {
    if (event) {
      this.teamSearchComponent.focus();
    }
  }

  selectStatus(statuses: QuickFilterOption<RockStatus | string>[]): void {
    const status = statuses.find(status => status.selected);
    this.filterService.setRockStatusFilter(status.item === 'all' ? 'all' : (<RockStatus>status.item).code);
  }

  getUserById(users: User[], userId: string): User {
    return users.find(user => user._id === userId);
  }

  onExplorePlansClick(): void {
    this.store.dispatch(BillingStateActions.openPricingPlansModal());
  }

  filterByUsers(users: User[]): void {
    const options = extractValueFromStore(this.store, selectUserFilterOptions);
    const allUsers = options.map(option => option.item);
    this.store.dispatch(
      UserListStateActions.changeFilterBarUsers({
        users,
        allUsers: allUsers.filter(user => user._id !== 'all'),
      })
    );
  }

  onUsersDropdownOpened(isOpen: boolean): void {
    if (isOpen) {
      return;
    }

    const options = extractValueFromStore(this.store, selectUserFilterOptions) ?? [];
    const hasSelection = options.some(opt => !!opt.selected);
    if (hasSelection) {
      return;
    }

    const allOpt = options.find(opt => opt.item?._id === 'all');
    if (allOpt?.item) {
      this.filterByUsers([allOpt.item]);
    }
  }

  compareUsers(user1: User, user2: User): boolean {
    return user1?._id === user2?._id;
  }

  viewHeadlinesTrash(): void {
    // HeadlinesComponent syncs showTrash from ?view=trash — navigate only to avoid double setShowTrash.
    void this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { view: 'trash' },
      queryParamsHandling: 'merge',
    });
  }

  viewIssuesTrash(): void {
    this.store.dispatch(LayoutsIssuesEvents.showTrashItems());
  }

  viewTodosTrash(): void {
    this.store.dispatch(LayoutsTodosEvents.showTrashItems());
  }
}
